import "../styles/daily.css";
import type { DailyEntry } from "@highmarch/domain";
import type { DailyRoadMode, LevelCatalog } from "@highmarch/app";
import { utcDay } from "@highmarch/domain";
import { dailyDayLabel } from "./dailyDate";
import { btnHtml, fmtCompact } from "../kit";
import { icon } from "../iconsKit";
import { checkHtml, sheetHeadHtml, wellHtml } from "../components";
import { evBannerHtml, evChipHtml, evKickHtml, factChipHtml } from "../hub/evKit";
import { SHOP_SKIN } from "../shopSkin";
import { portraitUrl, uiUrl } from "../portraits";
import { emberGlyph, marksGlyph } from "../hub/eventRewards";
import { fmtDay, fmtLeft } from "../hub/eventsSheet";
import { essenceIconHtml } from "../forgeArt";
import { paintedIconHtml } from "../featureIcons";

/** The sheet header's painted portrait (CSS px), as the Achievements and Almanac heads. */
const HEAD_ICON_PX = 52;

/** What the Daily Road sheet needs. */
export interface DailySheetCtx {
  readonly catalog: LevelCatalog;
  readonly daily: DailyRoadMode;
  readonly now: () => number;
  /** Starts today's road (the sheet goes first). */
  readonly start: () => void;
}

/** Reward art in the wells (CSS px). */
const WELL_ART_PX = 34;
/** The boss face on its chip. */
const BOSS_ART_PX = 20;
/** The VIP crown on its chip. */
const VIP_ART_PX = 16;
const WEEKDAYS = ["Thu", "Fri", "Sat", "Sun", "Mon", "Tue", "Wed"];

/** "Mon 12 Oct": a history row's day. Day 0 (1970-01-01) was a Thursday. */
function dayName(day: number): string { return `${WEEKDAYS[((day % 7) + 7) % 7]} ${fmtDay(day)}`; }

/** D1 (owner 2026-10-12): the Commander's own VIP damage rides along on the road; the chip says how much (none at VIP 0). */
function vipChip(ctx: DailySheetCtx): string {
  const pct = ctx.catalog.profile.vip.towerDamagePercent;
  return ctx.catalog.balance.modes.normalised.allowVip && pct > 0
    ? `<span class="evfact vip"><img decoding="sync" src="${uiUrl("icons/vip_points.png")}" alt="" width="${VIP_ART_PX}" height="${VIP_ART_PX}"> VIP +${pct}% damage</span>` : "";
}

function bannerHtml(ctx: DailySheetCtx): string {
  const B = ctx.catalog.balance, now = ctx.now(), road = ctx.daily.today(now), L = B.level(road.level), boss = B.enemies[L.bossEnemyIndex];
  const n = B.modes.normalised, art = portraitUrl(boss.id);
  const chips = `${evChipHtml(`${icon("clock", 13)}${fmtLeft(ctx.daily.endsAt(now) - now)}`)}${evChipHtml(dailyDayLabel(road.day))}`;
  return `${evBannerHtml({ scene: "evdaily", title: L.name, chips })}
    <div class="evfacts">${factChipHtml(icon("sword", 13), `${road.stages} waves`)}${factChipHtml(icon("tower", 13), `${n.towers.length} towers · lv ${n.towerLevel}`)}
      ${factChipHtml(art ? `<img decoding="sync" src="${art}" alt="" width="${BOSS_ART_PX}" height="${BOSS_ART_PX}">` : icon("skull", 13), boss.name)}${vipChip(ctx)}</div>`;
}

function bestsHtml(ctx: DailySheetCtx): string {
  const best = ctx.daily.best(ctx.now()), all = ctx.daily.history(ctx.now());
  const top = all.reduce((m, e) => Math.max(m, e.score), 0);
  return `<div class="drbests">
    <div class="drbest today"><small class="disp">TODAY</small><b class="disp">${best ? fmtCompact(best.score) : "—"}</b></div>
    <div class="drbest top"><small class="disp">BEST · ${ctx.daily.data.historyDays} DAYS</small><b class="disp">${top > 0 ? fmtCompact(top) : "—"}</b></div>
  </div>`;
}

function rewardHtml(ctx: DailySheetCtx): string {
  const d = ctx.daily.data, now = ctx.now(), paid = ctx.daily.paidToday(now), event = ctx.daily.liveEvent(now);
  const part = (art: string, name: string, count: string): string =>
    `<span class="evpart">${wellHtml({ art, count, dim: paid, tag: "span" })}<i class="evpn">${name}</i></span>`;
  const wells = [
    d.reward.essence > 0 ? part(essenceIconHtml(WELL_ART_PX), "Essence", fmtCompact(d.reward.essence)) : "",
    d.reward.marks > 0 ? part(marksGlyph(WELL_ART_PX), "Marks", fmtCompact(d.reward.marks)) : "",
    event && d.embers > 0 ? part(emberGlyph(WELL_ART_PX), event.currency, String(d.embers)) : "",
  ].join("");
  return `${evKickHtml("TODAY'S REWARD", paid ? "collected" : "first win")}
    <div class="drreward${paid ? " paid" : ""}"><div class="evparts">${wells}</div>${paid ? `<span class="drpaid">${checkHtml()}</span>` : ""}</div>`;
}

function historyRow(e: DailyEntry, today: number): string {
  const tag = e.won ? `<i class="res win disp">WIN</i>` : `<i class="res loss disp">LOSS</i>`;
  return `<div class="drday${e.day === today ? " today" : ""}"><span class="dt"><b>${e.day === today ? "Today" : dayName(e.day)}</b><small>${e.stages} waves · ${e.hp} HP · ${e.road}-tile road</small></span>${tag}<b class="sc disp">${fmtCompact(e.score)}</b></div>`;
}

function historyHtml(ctx: DailySheetCtx): string {
  const now = ctx.now(), rows = ctx.daily.history(now), today = utcDay(now);
  if (rows.length === 0) return "";
  return `${evKickHtml(`LAST ${ctx.daily.data.historyDays} DAYS`, `${rows.filter((e) => e.won).length} won`)}<div class="drhist">${rows.map((e) => historyRow(e, today)).join("")}</div>`;
}

/**
 * Events → Daily Road (MP-20, docs/MODES.md §4): today's road (the layout, its stages and boss, the fair rules), today's
 * best, the reward the first win pays (or that it is collected), PLAY, and the local history of the last days.
 */
export function showDailySheet(parent: HTMLElement, ctx: DailySheetCtx): HTMLElement {
  parent.querySelector(":scope > .sheet.daily")?.remove();
  const sheet = document.createElement("div");
  sheet.className = `sheet panel daily ${SHOP_SKIN}`;
  parent.append(sheet);
  const now = ctx.now(), paid = ctx.daily.paidToday(now);
  sheet.innerHTML = `${sheetHeadHtml({ title: "Daily Road", portrait: paintedIconHtml("dailyRoad", HEAD_ICON_PX), tint: "gold" })}
    <div class="drbody">${bannerHtml(ctx)}${rewardHtml(ctx)}
      <div class="drplay">${bestsHtml(ctx)}${btnHtml(paid ? "AGAIN" : "PLAY", "gold", { icon: "play", className: "disp go dailygo" })}</div>
      ${historyHtml(ctx)}</div>`;
  sheet.querySelector<HTMLButtonElement>(".close")!.onclick = () => sheet.remove();
  sheet.querySelector<HTMLButtonElement>(".dailygo")!.onclick = () => { sheet.remove(); ctx.start(); };
  return sheet;
}
